import { BluetoothErrorCode } from '../../../../backbone-unorganized/utils/bluetooth';
import { isAndroid, isWeb, isWindows } from '../../../../shared/utils/is';
import { ReadingMethod } from '../constants';
import { NumberFieldView } from '../numberField/views';
import { showErrorMessage } from '../../../../shared/utils/notifications';

export const TemperatureFieldView = NumberFieldView.extend({
    template: '#tpl_temperature_field',
    is_polling_field: false, // keeps track of when the fake_input should be selected
    last_polled_temp: null,

    events: _.extend(
        {
            'click .fake_input': 'click_probe_input',
            'click .connected .bluetherm_indicator': 'poll_start',
            'click .disconnected .bluetherm_indicator': 'retry_connect',
            'click .polling .bluetherm_indicator': 'click_set_value'
        },
        NumberFieldView.prototype.events
    ),

    templateHelpers: function () {
        var connection_state = 'disconnected';
        var i18n_key_placeholder = 'mobile_placeholder_therm_no_bluetooth';
        var is_testo = $f.mobile.bluetooth.device && $f.mobile.bluetooth.device.get('type') === 'testo';

        if ($f.mobile.bluetooth.supports_bluetooth()) {
            if ($f.mobile.bluetooth.is_connecting) {
                connection_state = 'connecting';
                i18n_key_placeholder = 'mobile_placeholder_blue_therm_connecting';
            } else if (this.is_polling_field) {
                connection_state = 'polling';
                i18n_key_placeholder = is_testo
                    ? 'mobile_placeholder_testo_tap'
                    : 'mobile_placeholder_blue_therm_please_wait';
            } else if ($f.mobile.bluetooth.is_connected) {
                connection_state = 'connected';
                i18n_key_placeholder = is_testo ? 'mobile_placeholder_testo_tap' : 'mobile_placeholder_blue_therm_tap';
            } else {
                // disconnected
                i18n_key_placeholder = 'mobile_placeholder_blue_therm_not_connected';
            }
        }

        return $.extend(
            {
                acceptance_value_class: this._get_acceptance_criteria_class(),
                temperature_value: this.get_company_temperature_value(),
                connection_state: connection_state,
                placeholder_text: _t(i18n_key_placeholder) || i18n_key_placeholder,
                is_force_probe_input: this._should_force_temperature_probe()
            },
            TemperatureFieldView.__super__.templateHelpers.apply(this, arguments)
        );
    },

    initialize: function (options) {
        TemperatureFieldView.__super__.initialize.apply(this, arguments);
        var this_ = this;

        // When any of these states change, we'll just re-render and handle the appropriate UI in the template
        this.listenTo($f.application.vent, 'btdevice:connected', function () {
            this_.render();
        });

        this.listenTo($f.application.vent, 'btdevice:disconnected btdevice:connecting', function () {
            this_.is_polling_field = false;
            this_.render();
        });

        this.listenTo($f.application.vent, 'btdevice:new_reading', function (temp) {
            if (!this_.is_polling_field || temp == -9999) {
                return;
            }
            this_.last_polled_temp = temp;

            if (!isNaN(parseFloat(temp))) {
                temp = $f.utils.get_rounded_temperature_in_configured_units(temp);
            }
            this_.$el.find('.fake_input').html(temp);
        });

        this.listenTo($f.application.vent, 'btdevice:button_pressed', function (temp) {
            // device button pressed
            if (!this_.is_polling_field || temp == -9999) {
                return;
            }
            this_._set_value(temp, ReadingMethod.PROBE);
        });
        this.listenTo(this.model, 'change:value', this.render);

        this.listenTo($f.application.vent, 'btdevice:clear_polling', function (check_view) {
            // exit if not polling field or if the view cid matches the currently active field
            if (!this_.is_polling_field || this_.cid === check_view.cid) {
                return;
            }
            // stop polling on any other fields
            this_.poll_stop();
        });
    },

    click_set_value: function (event) {
        this._set_value(this.last_polled_temp, ReadingMethod.PROBE);
        return false;
    },

    /**
     * The fake input field has two behaviors: if we're already connected to a probe, start polling. If not,
     * is_force_probe_input is true, so let's try connecting to one.
     */
    click_probe_input: function (event) {
        var this_ = this;
        $f.mobile.bluetooth.is_device_connected(function (is_connected) {
            if (!is_connected) {
                this_.retry_connect(event);
                return;
            }

            this_.poll_start(event);
        });

        return false;
    },

    _should_force_temperature_probe: function () {
        if (isWindows() || isWeb()) {
            return false;
        }

        return this.model.collection.metadata.get('is_force_probe_input');
    },

    /**
     * Move to the next visible field upon entering a value. So if this field generates a dependent text field, do nothing.
     *
     * Move to to the next visible field ONLY IF:
     *  - it is a temperature field
     *  - and it has not been previously answered
     */
    on_change_action: function (model) {
        // Ensure the action is setting a new temperature, not clearing an existing one as well.
        if (model.changed.value === null) {
            return;
        }

        var next_model = model.get_next_visible();
        // Note: if an answer has been answered and then cleared it is null, but if it has never been touched,
        // the value is ""
        if (
            !next_model ||
            next_model.get('type') != 'temperature' ||
            ['', null].indexOf(next_model.get('value')) == -1
        ) {
            return;
        }

        var $target = $('#field_' + next_model.get('id'));
        if (!$target) {
            return;
        }
        var $input = $target.find('.fake_input');
        if (!$input) {
            return;
        }
        $input.focus().click();
        // after this sets a value, the delete box comes up which changes the height of the current element,
        // by delaying shortly we move down the full height of this element
        setTimeout(this._scroll_down_one_field.bind(this), 100);
    },

    _set_value: function (temp, readingMethod) {
        if (!temp) {
            return;
        }

        this._scroll_down_one_field();
        this.model.set({ value: temp, reading_method: readingMethod });
        this.poll_stop();
    },

    save_value: function (event) {
        // remove commas from input before saving
        event.target.value = event.target.value.replace(/,/g, '');
        this.fix_event_val_leading_decimal(event);

        const input = $(event.target).val();
        this.$el.removeClass('invalid');
        const rounded_value = !isNaN(parseFloat(input))
            ? $f.utils.get_rounded_temperature_in_fahrenheit_units(input)
            : input;

        this.model.set({ value: rounded_value, reading_method: ReadingMethod.MANUAL });
        if (input != this.model.get('value')) {
            // rounded_value may be the same as value, so a change is not triggered but
            // the input value needs to be updated to show just 2 decimals
            this.render();
        }
    },

    poll_start: function (event) {
        var this_ = this;
        if (this.is_polling_field) {
            return false;
        }

        if (!$f.mobile.bluetooth.supports_bluetooth()) {
            return this._error_bluetooth_not_supported();
        }

        $f.mobile.bluetooth.is_device_connected(function (is_connected) {
            if (!is_connected) {
                this_.poll_stop();
                return;
            }

            // stop polling other fields here
            $f.application.vent.trigger('btdevice:clear_polling', this_);

            this_.is_polling_field = true;

            this_.render();
            $f.mobile.bluetooth.start_polling({
                error: function (error_code) {
                    if (error_code === BluetoothErrorCode.ERROR_PAIR_REQUEST_DENIED) {
                        // this will prompt the user to accept pairing again when they reconnect. Pairing manually in
                        // iOS settings doesn't seem to work.
                        $f.mobile.bluetooth.disconnect();
                    }

                    this_.is_polling_field = false;
                    this_.render();
                }
            });

            // Set up event so we can detect when the user clicks "out" of the fake_input
            $(window).on('click.bluetherm_stop_polling_' + this_.model.get('id'), function (_event) {
                var target = $(_event.target);
                if (target.hasClass('bluetherm_indicator') || target.parents('.bluetherm_indicator').length > 0) {
                    return true;
                } // make sure they're not pressing the save button
                this_.poll_stop();
                return false;
            });
        });

        return false;
    },

    poll_stop: function (event) {
        $(window).off('click.bluetherm_stop_polling_' + this.model.get('id'));

        if (!this.is_polling_field) {
            return false;
        }

        this.is_polling_field = false;
        $f.mobile.bluetooth.stop_polling();
        this.render(); // display the currently set value (even if empty)
        return false;
    },

    retry_connect: function (event) {
        if (!$f.mobile.bluetooth.supports_bluetooth()) {
            this._error_bluetooth_not_supported();
            return false;
        }

        // For some reason, the BLE plugin on Android removes focus from the webview, which
        // causes the form to reload if nothing's been changed yet.
        if (isAndroid()) {
            this.trigger('prevent_next_reload_on_resume');
        }

        $f.mobile.bluetooth.connect();
        return false;
    },

    _error_bluetooth_not_supported: function () {
        if (isWeb()) {
            showErrorMessage(_t('mobile_alert_please_install_for_native'));
        } else {
            showErrorMessage(_t('mobile_windows_10_bluetherm_not_supported'));
        }
    },

    onClose: function () {
        $(window).off('click.bluetherm_stop_polling_' + this.model.get('id'));
    },

    get_company_temperature_value: function () {
        const value = this.model.get('value');
        return !isNaN(parseFloat(value)) ? $f.utils.get_rounded_temperature_in_configured_units(value) : value;
    }
});
